import { useEffect, useState } from "react";
import styles from "./EditUS30Page.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { useVideoTabs } from "../../hooks/useVideoTabs";
import VideoTabsManager from "../../components/VideoTabsManager";

const EditUs30ContentPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [us30, setUs30] = useState({
    title: "",
    primaryText: "",
    secondaryText: "",
    bannerFile: null,
    bannerUrl: "",
    deleteBanner: false,
    founderImageFile: null,
    founderImageUrl: "",
    deleteFounderImage: false,
  });

  const videoTabsState = useVideoTabs("us-30");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_US30_CONTENT);

        if (data.success) {
          const c = data.content;

          setUs30({
            title: c.title_text ?? "",
            primaryText: c.description_text ?? "",
            secondaryText: c.secondary_text ?? "",
            bannerFile: null,
            bannerUrl: c.banner_image
              ? ENDPOINTS.IMAGE_BASE_URL + c.banner_image
              : "",
            deleteBanner: false,
            founderImageFile: null,
            founderImageUrl: c.founder_image
              ? ENDPOINTS.IMAGE_BASE_URL + c.founder_image
              : "",
            deleteFounderImage: false,
          });

          // Load video tabs — prefer new key, fallback to old for safety
          const tabs = c.video_tabs || c.video_links || [];
          const loadedTabs = tabs.map((tab) => ({
            ...tab,
            groups: (tab.groups || []).map((group) => ({
              ...group,
              group_banner_mobile_url: group.group_banner_mobile_url
                ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_mobile_url
                : null,
              group_banner_web_url: group.group_banner_web_url
                ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_web_url
                : null,
              videos: (group.videos || []).map((video) => ({
                ...video,
                cover_image: video.cover_image
                  ? ENDPOINTS.IMAGE_BASE_URL + video.cover_image
                  : null,
                attachment_file: null,
                attachment_file_url: video.attach_file
                  ? ENDPOINTS.IMAGE_BASE_URL + video.attach_file
                  : null,
                // membership_expiry array comes from API as-is
                membership_expiry: video.membership_expiry || [],
              })),
            })),
          }));
          videoTabsState.setVideoTabs(loadedTabs);
        }
      } catch (e) {
        toast.error("Failed to load US30 content");
      }
    };

    fetchData();
  }, []);

  const handleImageUpload = (keyFile, keyUrl, deleteKey, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUs30((prev) => ({
      ...prev,
      [keyFile]: file,
      [keyUrl]: URL.createObjectURL(file),
      [deleteKey]: false,
    }));
  };

  const handleImageDelete = (keyFile, keyUrl, deleteKey) => {
    setUs30((prev) => ({
      ...prev,
      [keyFile]: null,
      [keyUrl]: "",
      [deleteKey]: true,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("title_text", us30.title);
      formData.append("description_text", us30.primaryText);
      formData.append("secondary_text", us30.secondaryText);

      if (us30.bannerFile) formData.append("banner_image", us30.bannerFile);
      if (us30.deleteBanner) formData.append("delete_banner_image", "true");

      if (us30.founderImageFile)
        formData.append("founder_image", us30.founderImageFile);
      if (us30.deleteFounderImage)
        formData.append("delete_founder_image", "true");

      // Video cover files
      videoTabsState.videoCoverFiles.forEach((file) => {
        formData.append("video_covers", file);
      });

      // Collect all attachment files in order
      let attachments = [];
      let groupBanners = [];
      (videoTabsState?.videoTabs || []).forEach((tab) => {
        (tab?.groups || []).forEach((group) => {
          if (group?.group_banner_mobile_file) {
            groupBanners.push(group.group_banner_mobile_file);
          }
          if (group?.group_banner_web_file) {
            groupBanners.push(group.group_banner_web_file);
          }

          (group?.videos || []).forEach((video) => {
            if (video?.attachment_file) {
              attachments.push(video.attachment_file);
            }
          });
        });
      });
      attachments.forEach((file) => formData.append("video_attachments", file));

  // Append all group banner files
      groupBanners.forEach((file) => {
        formData.append("group_files", file);
      });

      // Video tabs structure
      if (videoTabsState.videoTabs.length > 0) {
        const sortedTabs = videoTabsState.videoTabs.map((tab, tabIdx) => ({
          ...tab,
          sort_order: tabIdx,
         groups: (tab.groups || []).map((group, groupIdx) => {
            // Mobile
            let cleanGroupMobileUrl = group.group_banner_mobile_url;
            if (cleanGroupMobileUrl && cleanGroupMobileUrl.includes("/uploads/")) {
              const parts = cleanGroupMobileUrl.split("/uploads/");
              cleanGroupMobileUrl = parts[parts.length - 1];
            }

            let group_banner_mobile_index = null;
            if (group.group_banner_mobile_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_mobile_file?.name &&
                  f?.size === group.group_banner_mobile_file?.size
              );
              group_banner_mobile_index = index !== -1 ? index : null;
            }

            // Web
            let cleanGroupWebUrl = group.group_banner_web_url;
            if (cleanGroupWebUrl && cleanGroupWebUrl.includes("/uploads/")) {
              const parts = cleanGroupWebUrl.split("/uploads/");
              cleanGroupWebUrl = parts[parts.length - 1];
            }

            let group_banner_web_index = null;
            if (group.group_banner_web_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_web_file?.name &&
                  f?.size === group.group_banner_web_file?.size
              );
              group_banner_web_index = index !== -1 ? index : null;
            }

            return {
              ...group,
              sort_order: groupIdx,
              group_banner_mobile_url: cleanGroupMobileUrl,
              group_banner_mobile_index: group_banner_mobile_index,
              group_banner_web_url: cleanGroupWebUrl,
              group_banner_web_index: group_banner_web_index,
              videos: (group.videos || []).map((video, videoIdx) => {
                let cleanCoverImage = video.cover_image;
                if (cleanCoverImage && cleanCoverImage.includes("/uploads/")) {
                  const parts = cleanCoverImage.split("/uploads/");
                  cleanCoverImage = parts[parts.length - 1];
                }

                let cleanAttachedFileUrl = video.attachment_file_url;
                if (cleanAttachedFileUrl && cleanAttachedFileUrl.includes("/uploads/")) {
                  const parts = cleanAttachedFileUrl.split("/uploads/");
                  cleanAttachedFileUrl = parts[parts.length - 1];
                }

                let attach_file_index = null;
                if (video.attachment_file) {
                  const index = attachments.findIndex(
                    (f) => f?.name === video.attachment_file?.name &&
                      f?.size === video.attachment_file?.size
                  );
                  attach_file_index = index !== -1 ? index : null;
                }

                return {
                  id: video.id ?? null,
                  title: video.title,
                  url: video.url,
                  video_type: video.video_type,
                  membership_ids: video.membership_ids || [],
                  sort_order: videoIdx,
                  cover_image: cleanCoverImage,
                  cover_image_index: video.cover_image_index,
                  membership_expiry: video.membership_expiry || [],
                  attach_file: cleanAttachedFileUrl,
                  attach_file_index,
                };
              }),
            }
          }),
        }));

        formData.append("video_tabs", JSON.stringify(sortedTabs));
      }

      const { data } = await api.put(ENDPOINTS.GET_US30_CONTENT, formData);

      if (data.success) {
        toast.success("US30 content updated successfully");
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (e) {
      toast.error("Error updating US30 content");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>Edit US30 Content</h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Banner */}
          <label className={styles.inlineLabel}>Banner Image</label>
          <div className={styles.featuredImageBox}>
            {us30.bannerUrl ? (
              <>
                <img
                  src={us30.bannerUrl}
                  alt="Banner Image"
                  className={styles.featuredImageBox}
                />
                <button
                  type="button"
                  disabled={isSubmitting}
                  title="Remove image"
                  className={styles.removeImageButton}
                  onClick={() =>
                    handleImageDelete("bannerFile", "bannerUrl", "deleteBanner")
                  }
                >
                  ✕
                </button>
              </>
            ) : (
              <div className={styles.iconPlaceholderContainer}>
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.iconPlaceholder}
                />
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Banner Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      handleImageUpload(
                        "bannerFile",
                        "bannerUrl",
                        "deleteBanner",
                        e,
                      )
                    }
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title</label>
            <input
              type="text"
              value={us30.title}
              onChange={(e) => setUs30({ ...us30, title: e.target.value })}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Primary Description */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Primary Description</label>
            <textarea
              value={us30.primaryText}
              onChange={(e) =>
                setUs30({ ...us30, primaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Enter Primary Description"
              disabled={isSubmitting}
            />
          </div>

          {/* Founder Image */}
          <label className={styles.inlineLabel}>Founder Image</label>
          <div className={styles.featuredImageBox}>
            {us30.founderImageUrl ? (
              <>
                <img
                  src={us30.founderImageUrl}
                  alt="Founder Image"
                  className={styles.featuredImageBox}
                />
                <button
                  type="button"
                  disabled={isSubmitting}
                  title="Remove image"
                  className={styles.removeImageButton}
                  onClick={() =>
                    handleImageDelete(
                      "founderImageFile",
                      "founderImageUrl",
                      "deleteFounderImage",
                    )
                  }
                >
                  ✕
                </button>
              </>
            ) : (
              <div className={styles.iconPlaceholderContainer}>
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.iconPlaceholder}
                />
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Founder
                  Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      handleImageUpload(
                        "founderImageFile",
                        "founderImageUrl",
                        "deleteFounderImage",
                        e,
                      )
                    }
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Secondary Description */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Secondary Text</label>
            <textarea
              value={us30.secondaryText}
              onChange={(e) =>
                setUs30({ ...us30, secondaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Enter Secondary Text"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <VideoTabsManager
            videoTabsState={videoTabsState}
            isSubmitting={isSubmitting}
          />

          <button
            className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ""}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <span>{isSubmitting ? "Updating..." : "Update US30 Content"}</span>
            <span className={styles.submitIcon}>✓</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUs30ContentPage;
